"use client";
import { useState, useEffect, useMemo, Fragment } from "react";
import axios from "axios";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

// ─── Formatting ─────────────────────────────────────────────────────────────
const cls = (...c) => c.filter(Boolean).join(" ");
const pad = (n) => String(n).padStart(2, "0");
const localISO = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Accounting style: negatives in parentheses
const money = (n, decimals = 2) => {
  const v = Number(n || 0);
  const s = Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return v < -0.004 ? `($${s})` : `$${s}`;
};
const money0 = (n) => money(n, 0);
const pctText = (n) =>
  n == null || !isFinite(n) ? "—" : `${Number(n).toFixed(1)}%`;

// Dates arrive as "YYYY-MM-DD" calendar days — format in UTC so they
// never shift a day in Toronto time.
const fmtDay = (s) => {
  if (!s) return "—";
  const [y, m, d] = String(s).split("-").map(Number);
  if (!y) return "—";
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};
const monthBounds = (key) => {
  const [y, m] = key.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return [`${key}-01`, `${key}-${pad(last)}`];
};

const sum = (arr, k) => arr.reduce((s, x) => s + (Number(x[k]) || 0), 0);

function downloadCsv(filename, header, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
const csvNum = (n) => (n == null || !isFinite(n) ? "" : Number(n).toFixed(2));

// Aging palette runs from settled green to alarm red
// Aging palette runs from settled green to alarm red
const AGING_BAR = [
  "bg-emerald-500",
  "bg-yellow-400",
  "bg-orange-400",
  "bg-red-400",
  "bg-red-600",
];
const AGING_TEXT = [
  "text-emerald-400",
  "text-yellow-300",
  "text-orange-300",
  "text-red-300",
  "text-red-400",
];
// 6-bucket palette for the receivables collection ladder
const AGING_BAR_6 = [
  "bg-emerald-500",
  "bg-yellow-400",
  "bg-amber-400",
  "bg-orange-500",
  "bg-red-500",
  "bg-red-700",
];
const AGING_TEXT_6 = [
  "text-emerald-400",
  "text-yellow-300",
  "text-amber-300",
  "text-orange-400",
  "text-red-400",
  "text-red-500",
];

// Receivables collection ladder
const AR_BUCKETS = [
  { label: "0–35", action: "No action" },
  { label: "36–45", action: "Follow up / request expected payment date" },
  { label: "46–60", action: "Confirm upstream billing date + payment status" },
  {
    label: "61–75",
    action: "Escalate + confirm whether Notice of Non-Payment exists",
  },
  { label: "76–90", action: "Management review / protect statutory rights" },
  { label: "90+", action: "Critical collection/legal review" },
];
const arBucketIndex = (age) =>
  age <= 35
    ? 0
    : age <= 45
      ? 1
      : age <= 60
        ? 2
        : age <= 75
          ? 3
          : age <= 90
            ? 4
            : 5;
const dayNum = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return Date.UTC(y, m - 1, d) / 86400000;
};

const INPUT =
  "bg-gray-900 border border-gray-700 text-gray-200 text-sm rounded-lg px-2.5 py-1.5 [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-orange-400";

// ─── Data hook ──────────────────────────────────────────────────────────────
function useReport(path, params, token, enabled = true) {
  const [state, setState] = useState({
    data: null,
    loading: true,
    error: null,
  });
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    axios
      .get(`${API_BASE}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      })
      .then((res) => {
        if (cancelled) return;
        if (res.data.success)
          setState({ data: res.data, loading: false, error: null });
        else
          setState((s) => ({
            ...s,
            loading: false,
            error: res.data.message || "The report didn't load.",
          }));
      })
      .catch((err) => {
        if (!cancelled)
          setState((s) => ({
            ...s,
            loading: false,
            error: err.response?.data?.message || err.message,
          }));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, token, enabled]);
  return state;
}

// ─── Shared UI ──────────────────────────────────────────────────────────────
function Segmented({ options, value, onChange, size = "sm" }) {
  return (
    <div className="inline-flex bg-gray-900 border border-gray-700 rounded-lg p-0.5">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={cls(
            "rounded-md font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400",
            size === "sm" ? "px-2.5 py-1 text-xs" : "px-3.5 py-1.5 text-sm",
            value === o.key
              ? "bg-orange-500 text-white"
              : "text-gray-400 hover:text-white",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-gray-400">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-gray-500">{hint}</span>}
    </label>
  );
}

function Stat({ label, value, sub, tone = "text-white" }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-gray-400">{label}</div>
      <div
        className={cls(
          "text-2xl font-semibold tabular-nums tracking-tight mt-1",
          tone,
        )}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function Card({ children, className }) {
  return (
    <section
      className={cls(
        "bg-gray-800 border border-gray-700 rounded-xl",
        className,
      )}
    >
      {children}
    </section>
  );
}

function Notice({ tone = "error", children }) {
  return (
    <div
      className={cls(
        "rounded-lg px-4 py-3 text-sm border",
        tone === "error"
          ? "bg-red-950 border-red-700/40 text-red-300"
          : "bg-yellow-900/20 border-yellow-700/40 text-yellow-200",
      )}
    >
      {children}
    </div>
  );
}

function SortTh({ label, k, sort, onSort, align = "right" }) {
  const active = sort.key === k;
  return (
    <th
      className={cls(
        "px-3 py-2.5 whitespace-nowrap",
        align === "right" ? "text-right" : "text-left",
      )}
    >
      <button
        type="button"
        onClick={() => onSort(k)}
        className={cls(
          "text-xs font-medium hover:text-white",
          active ? "text-orange-300" : "text-gray-400",
        )}
      >
        {label}
        {active ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
      </button>
    </th>
  );
}

function sortRows(rows, sort) {
  return [...rows].sort((a, b) => {
    const av = a[sort.key];
    const bv = b[sort.key];
    const r =
      typeof av === "number" || typeof bv === "number"
        ? (av ?? -Infinity) - (bv ?? -Infinity)
        : String(av ?? "").localeCompare(String(bv ?? ""));
    return sort.dir === "asc" ? r : -r;
  });
}
const nextSort = (s, key) =>
  s.key === key
    ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
    : { key, dir: "desc" };

// Aging band — the bucket split at a glance; click a bucket to filter
function AgingBand({ labels, amounts, active, onSelect, actions }) {
  const bars = labels.length === 6 ? AGING_BAR_6 : AGING_BAR;
  const total = amounts.reduce((s, a) => s + Math.max(0, a), 0);
  return (
    <div>
      <div className="flex h-3 rounded-full overflow-hidden bg-gray-700">
        {total > 0 &&
          amounts.map((a, i) =>
            a > 0 ? (
              <button
                key={i}
                type="button"
                onClick={() => onSelect(active === i ? null : i)}
                aria-label={`${labels[i]}: ${money0(a)}`}
                className={cls(
                  bars[i],
                  "transition-opacity",
                  active != null && active !== i && "opacity-25",
                )}
                style={{ width: `${(a / total) * 100}%` }}
              />
            ) : null,
          )}
      </div>
      <div
        className={cls(
          "grid grid-cols-2 gap-2 mt-3",
          labels.length === 6
            ? "sm:grid-cols-3 lg:grid-cols-6"
            : "sm:grid-cols-5",
        )}
      >
        {labels.map((l, i) => (
          <button
            key={l}
            type="button"
            onClick={() => onSelect(active === i ? null : i)}
            aria-pressed={active === i}
            className={cls(
              "text-left rounded-lg px-3 py-2 border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400",
              active === i
                ? "border-orange-500/60 bg-orange-500/10"
                : "border-gray-700 hover:border-gray-500",
            )}
          >
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <span className={cls("w-2 h-2 rounded-sm", bars[i])} />
              {l}
            </div>
            <div
              className={cls(
                "text-base font-semibold tabular-nums mt-0.5",
                amounts[i] ? "text-gray-100" : "text-gray-600",
              )}
            >
              {money0(amounts[i])}
            </div>
            <div className="text-[11px] text-gray-500">
              {total > 0
                ? `${((Math.max(0, amounts[i]) / total) * 100).toFixed(0)}% of balance`
                : "—"}
            </div>
            {actions?.[i] && (
              <div className="text-[11px] text-gray-400 mt-1 leading-snug">
                {actions[i]}
              </div>
            )}
          </button>
        ))}
      </div>
      {active != null && (
        <button
          type="button"
          onClick={() => onSelect(null)}
          className="mt-2 text-xs text-orange-300 hover:text-orange-200"
        >
          Showing {labels[active].toLowerCase()} only · Show all buckets
        </button>
      )}
    </div>
  );
}

// Group open items into an aging summary (customer or vendor), with sub rows
function groupAging(items, { key, name, subKey, subName, size = 5 }) {
  const map = new Map();
  items.forEach((i) => {
    const k = key(i);
    if (!map.has(k))
      map.set(k, {
        key: k,
        name: name(i),
        buckets: Array(size).fill(0),
        total: 0,
        count: 0,
        subs: new Map(),
      });
    const g = map.get(k);
    g.buckets[i.bucket] += i.balance;
    g.total += i.balance;
    g.count += 1;
    const sk = subKey(i);
    if (!g.subs.has(sk))
      g.subs.set(sk, {
        key: sk,
        name: subName(i),
        buckets: Array(size).fill(0),
        total: 0,
        count: 0,
        items: [],
      });
    const s = g.subs.get(sk);
    s.buckets[i.bucket] += i.balance;
    s.total += i.balance;
    s.count += 1;
    s.items.push(i);
  });
  return [...map.values()]
    .map((g) => ({
      ...g,
      subs: [...g.subs.values()].map((s) => ({
        ...s,
        items: s.items.sort(
          (a, b) => (b.daysPastDue ?? 0) - (a.daysPastDue ?? 0),
        ),
      })),
    }))
    .sort((a, b) => b.total - a.total);
}

function AgingSummaryTable({
  groups,
  labels,
  entity,
  countLabel,
  expanded,
  onToggle,
  drill = false,
}) {
  const texts = labels.length === 6 ? AGING_TEXT_6 : AGING_TEXT;
  const bars = labels.length === 6 ? AGING_BAR_6 : AGING_BAR;
  const foot = labels.map((_, i) =>
    groups.reduce((s, g) => s + g.buckets[i], 0),
  );
  const footTotal = groups.reduce((s, g) => s + g.total, 0);
  const footCount = groups.reduce((s, g) => s + g.count, 0);
  const cell = (b, base, zero) =>
    cls(base, Math.abs(b) < 0.005 ? zero : "text-gray-300");
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-900/80">
          <tr>
            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400">
              {entity}
            </th>
            {labels.map((l, i) => (
              <th
                key={l}
                className="px-3 py-2.5 text-right text-xs font-medium whitespace-nowrap"
              >
                <span className={texts[i]}>{l}</span>
              </th>
            ))}
            <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-300">
              Total
            </th>
            <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-400">
              {countLabel}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700/60">
          {groups.map((g) => {
            const open = expanded.has(g.key);
            return (
              <Fragment key={g.key}>
                <tr
                  onClick={() => onToggle(g.key)}
                  className={cls(
                    "cursor-pointer",
                    open ? "bg-gray-700/30" : "hover:bg-gray-700/20",
                  )}
                >
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2 min-w-[200px]">
                      <svg
                        className={cls(
                          "w-3.5 h-3.5 shrink-0 transition-transform",
                          open ? "rotate-90 text-orange-300" : "text-gray-500",
                        )}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                      <span className="text-gray-100 font-medium">
                        {g.name}
                      </span>
                    </div>
                  </td>
                  {g.buckets.map((b, i) => (
                    <td
                      key={i}
                      className={cls(
                        "px-3 py-2.5 text-right tabular-nums",
                        Math.abs(b) < 0.005 ? "text-gray-600" : "text-gray-200",
                      )}
                    >
                      {Math.abs(b) < 0.005 ? "—" : money(b)}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-white">
                    {money(g.total)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-400">
                    {g.count}
                  </td>
                </tr>
                {open &&
                  g.subs.map((s) => {
                    const subId = `${g.key}|${s.key}`;
                    const subOpen = drill && expanded.has(subId);
                    return (
                      <Fragment key={s.key}>
                        <tr
                          onClick={drill ? () => onToggle(subId) : undefined}
                          className={cls(
                            "bg-gray-900/40",
                            drill && "cursor-pointer hover:bg-gray-900/70",
                          )}
                        >
                          <td className="pl-9 pr-4 py-2 text-xs text-gray-300">
                            <div className="flex items-center gap-1.5">
                              {drill && (
                                <svg
                                  className={cls(
                                    "w-3 h-3 shrink-0 transition-transform",
                                    subOpen
                                      ? "rotate-90 text-orange-300"
                                      : "text-gray-500",
                                  )}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              )}
                              {s.name}
                            </div>
                          </td>
                          {s.buckets.map((b, i) => (
                            <td
                              key={i}
                              className={cell(
                                b,
                                "px-3 py-2 text-right text-xs tabular-nums",
                                "text-gray-700",
                              )}
                            >
                              {Math.abs(b) < 0.005 ? "—" : money(b)}
                            </td>
                          ))}
                          <td className="px-3 py-2 text-right text-xs tabular-nums text-gray-200">
                            {money(s.total)}
                          </td>
                          <td className="px-4 py-2 text-right text-xs text-gray-500">
                            {s.count}
                          </td>
                        </tr>
                        {subOpen &&
                          s.items.map((it) => (
                            <tr key={it.id} className="bg-gray-950/50">
                              <td className="pl-16 pr-4 py-1.5 text-[11px] text-gray-400">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={cls(
                                      "w-1.5 h-3.5 rounded-sm shrink-0",
                                      bars[it.bucket],
                                    )}
                                    title={labels[it.bucket]}
                                  />
                                  <span className="text-gray-200">
                                    {it.invoiceNumber}
                                  </span>
                                  <span className="text-gray-500">
                                    {it.kind} · invoiced{" "}
                                    {fmtDay(it.invoiceDate)} · due{" "}
                                    {fmtDay(it.dueDate)}
                                  </span>
                                </div>
                              </td>
                              {labels.map((_, i) => (
                                <td
                                  key={i}
                                  className={cls(
                                    "px-3 py-1.5 text-right text-[11px] tabular-nums",
                                    i === it.bucket
                                      ? "text-gray-100"
                                      : "text-gray-800",
                                  )}
                                >
                                  {i === it.bucket ? money(it.balance) : "—"}
                                </td>
                              ))}
                              <td className="px-3 py-1.5 text-right text-[11px] tabular-nums text-gray-200">
                                {money(it.balance)}
                              </td>
                              <td className="px-4 py-1.5 text-right text-[11px] whitespace-nowrap">
                                <PastDue days={it.daysPastDue} />
                              </td>
                            </tr>
                          ))}
                      </Fragment>
                    );
                  })}
              </Fragment>
            );
          })}
          {groups.length === 0 && (
            <tr>
              <td
                colSpan={labels.length + 3}
                className="px-4 py-12 text-center text-sm text-gray-500"
              >
                Nothing outstanding for these filters.
              </td>
            </tr>
          )}
        </tbody>
        {groups.length > 0 && (
          <tfoot className="bg-gray-900/80 border-t border-gray-600">
            <tr>
              <td className="px-4 py-3 text-sm font-semibold text-white">
                Total
              </td>
              {foot.map((b, i) => (
                <td
                  key={i}
                  className="px-3 py-3 text-right tabular-nums font-semibold text-gray-100"
                >
                  {money(b)}
                </td>
              ))}
              <td className="px-3 py-3 text-right tabular-nums font-semibold text-orange-300">
                {money(footTotal)}
              </td>
              <td className="px-4 py-3 text-right text-gray-400">
                {footCount}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function AgingControls({
  asOf,
  setAsOf,
  ageBy,
  setAgeBy,
  terms,
  setTerms,
  search,
  setSearch,
  placeholder,
  children,
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <Field label="As of">
        <input
          type="date"
          value={asOf}
          onChange={(e) => e.target.value && setAsOf(e.target.value)}
          className={INPUT}
        />
      </Field>
      <Field label="Age by">
        <Segmented
          value={ageBy}
          onChange={setAgeBy}
          options={[
            { key: "due", label: "Days past due" },
            { key: "invoice", label: "Invoice date" },
          ]}
        />
      </Field>
      <Field label="Default terms">
        <select
          value={terms}
          onChange={(e) => setTerms(Number(e.target.value))}
          className={INPUT}
          title="Used when a document has no due date"
        >
          {[0, 15, 30, 45, 60, 90].map((t) => (
            <option key={t} value={t}>
              {t === 0 ? "Due on receipt" : `Net ${t}`}
            </option>
          ))}
        </select>
      </Field>
      {children}
      <Field label="Search">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder}
          className={cls(INPUT, "w-56 placeholder:text-gray-500")}
        />
      </Field>
    </div>
  );
}

function ExportButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
    >
      Export CSV
    </button>
  );
}

function PastDue({ days }) {
  if (days <= 0)
    return (
      <span className="text-gray-500">
        {days === 0 ? "Due today" : `Due in ${Math.abs(days)}d`}
      </span>
    );
  return (
    <span
      className={
        days > 60
          ? "text-red-400 font-medium"
          : days > 30
            ? "text-orange-300"
            : "text-yellow-300"
      }
    >
      {days}d late
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ACCOUNTS RECEIVABLE
// ════════════════════════════════════════════════════════════════════════════
function ReceivablesReport({ token }) {
  const [asOf, setAsOf] = useState(localISO(new Date()));
  const [ageBy, setAgeBy] = useState("due");
  const [terms, setTerms] = useState(30);
  const [view, setView] = useState("customers");
  const [bucket, setBucket] = useState(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [sort, setSort] = useState({ key: "daysPastDue", dir: "desc" });

  const { data, loading, error } = useReport(
    "/finance3/ar",
    { asOf, ageBy, terms },
    token,
  );
  const labels = AR_BUCKETS.map((b) => b.label);
  const actions = AR_BUCKETS.map((b) => b.action);

  // Re-bucket on the client into the collection ladder
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    const asOfDay = dayNum(data?.asOf || asOf);
    return (data?.invoices || [])
      .map((i) => {
        const age =
          ageBy === "invoice"
            ? Math.floor(asOfDay - dayNum(i.invoiceDate))
            : i.daysPastDue;
        return { ...i, age, bucket: arBucketIndex(age) };
      })
      .filter(
        (i) =>
          !q ||
          [i.customer, i.projectName, i.projectNumber, i.invoiceNumber].some(
            (v) =>
              String(v ?? "")
                .toLowerCase()
                .includes(q),
          ),
      );
  }, [data, search, ageBy, asOf]);

  const bucketTotals = useMemo(() => {
    const a = Array(AR_BUCKETS.length).fill(0);
    searched.forEach((i) => (a[i.bucket] += i.balance));
    return a;
  }, [searched]);

  const visible = useMemo(
    () =>
      bucket == null ? searched : searched.filter((i) => i.bucket === bucket),
    [searched, bucket],
  );

  const customers = useMemo(
    () =>
      groupAging(visible, {
        key: (i) => i.customerId,
        name: (i) => i.customer,
        subKey: (i) => String(i.projectId),
        subName: (i) => `#${i.projectNumber ?? "—"} ${i.projectName}`,
        size: AR_BUCKETS.length,
      }),
    [visible],
  );

  const holdback = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.holdback || []).filter(
      (h) =>
        !q ||
        [h.customer, h.projectName, h.projectNumber].some((v) =>
          String(v ?? "")
            .toLowerCase()
            .includes(q),
        ),
    );
  }, [data, search]);

  const sortedInvoices = useMemo(
    () => sortRows(visible, sort),
    [visible, sort],
  );

  const totalOpen = sum(searched, "balance");
  const pastDue = searched.filter((i) => i.daysPastDue > 0);
  const holdbackTotal = sum(holdback, "totalReceivable");

  const toggle = (k) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });

  const exportCsv = () => {
    const stamp = `ar-${view}-${asOf}.csv`;
    if (view === "customers") {
      const rows = [];
      customers.forEach((g) => {
        rows.push([
          g.name,
          "",
          ...g.buckets.map(csvNum),
          csvNum(g.total),
          g.count,
        ]);
        g.subs.forEach((s) =>
          rows.push([
            g.name,
            s.name,
            ...s.buckets.map(csvNum),
            csvNum(s.total),
            s.count,
          ]),
        );
      });
      downloadCsv(
        stamp,
        ["Customer", "Project", ...labels, "Total", "Invoices"],
        rows,
      );
    } else if (view === "invoices") {
      downloadCsv(
        stamp,
        [
          "Invoice",
          "Customer",
          "Project #",
          "Project",
          "Type",
          "Invoice date",
          "Due date",
          "Days past due",
          "Aging bucket",
          "Age (days)",
          "Recommended action",
          "Invoice total",
          "Paid",
          "Balance",
        ],
        sortedInvoices.map((i) => [
          i.invoiceNumber,
          i.customer,
          i.projectNumber,
          i.projectName,
          i.kind,
          i.invoiceDate,
          i.dueDate,
          i.daysPastDue,
          labels[i.bucket],
          i.age,
          actions[i.bucket],
          csvNum(i.total),
          csvNum(i.paid),
          csvNum(i.balance),
        ]),
      );
    } else {
      downloadCsv(
        stamp,
        [
          "Project #",
          "Project",
          "Customer",
          "Held on draws",
          "Released",
          "Draw holdback receivable",
          "CO invoice holdback",
          "Total holdback receivable",
        ],
        holdback.map((h) => [
          h.projectNumber,
          h.projectName,
          h.customer,
          csvNum(h.progressHeld),
          csvNum(h.released),
          csvNum(h.receivable),
          csvNum(h.standaloneReceivable),
          csvNum(h.totalReceivable),
        ]),
      );
    }
  };

  return (
    <div className="space-y-6">
      <AgingControls
        asOf={asOf}
        setAsOf={setAsOf}
        ageBy={ageBy}
        setAgeBy={setAgeBy}
        terms={terms}
        setTerms={setTerms}
        search={search}
        setSearch={setSearch}
        placeholder="Customer, project or invoice"
      />

      {error && <Notice>{error}</Notice>}
      {!data && loading && (
        <div className="py-16 text-center text-sm text-gray-400">
          Loading receivables…
        </div>
      )}

      {data && (
        <div
          className={cls(
            "space-y-6 transition-opacity",
            loading && "opacity-60",
          )}
        >
          <Card className="p-5 space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <Stat
                label="Accounts receivable"
                value={money0(totalOpen)}
                sub={`${searched.length} open invoices as of ${fmtDay(data.asOf)}`}
                tone="text-orange-300"
              />
              <Stat
                label="Past due"
                value={money0(sum(pastDue, "balance"))}
                sub={
                  totalOpen > 0
                    ? `${pastDue.length} invoices · ${((sum(pastDue, "balance") / totalOpen) * 100).toFixed(0)}% of AR`
                    : "Nothing past due"
                }
                tone={pastDue.length ? "text-red-400" : "text-emerald-400"}
              />
              <Stat
                label="Holdback receivable"
                value={money0(holdbackTotal)}
                sub="Retained, not yet invoiced for release"
                tone="text-purple-300"
              />
              <Stat
                label="Days sales outstanding"
                value={
                  data.stats.dso == null
                    ? "—"
                    : `${Math.round(data.stats.dso)} days`
                }
                sub={`On ${money0(data.stats.billedLast90)} billed in the last 90 days`}
              />
            </div>
            <AgingBand
              labels={labels}
              amounts={bucketTotals}
              active={bucket}
              onSelect={setBucket}
              actions={actions}
            />
          </Card>

          <Card className="overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-700 flex flex-wrap items-center justify-between gap-3">
              <Segmented
                value={view}
                onChange={setView}
                options={[
                  { key: "customers", label: "Aging by customer" },
                  { key: "invoices", label: "Open invoices" },
                  { key: "holdback", label: "Holdback receivable" },
                ]}
              />
              <ExportButton
                onClick={exportCsv}
                disabled={
                  view === "holdback" ? !holdback.length : !visible.length
                }
              />
            </div>

            {view === "customers" && (
              <AgingSummaryTable
                groups={customers}
                labels={labels}
                entity="Customer"
                countLabel="Invoices"
                expanded={expanded}
                onToggle={toggle}
                drill
              />
            )}

            {view === "invoices" && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900/80">
                    <tr>
                      <SortTh
                        label="Invoice"
                        k="invoiceNumber"
                        sort={sort}
                        onSort={(k) => setSort((s) => nextSort(s, k))}
                        align="left"
                      />
                      <SortTh
                        label="Customer"
                        k="customer"
                        sort={sort}
                        onSort={(k) => setSort((s) => nextSort(s, k))}
                        align="left"
                      />
                      <SortTh
                        label="Project"
                        k="projectNumber"
                        sort={sort}
                        onSort={(k) => setSort((s) => nextSort(s, k))}
                        align="left"
                      />
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-400">
                        Type
                      </th>
                      <SortTh
                        label="Invoiced"
                        k="invoiceDate"
                        sort={sort}
                        onSort={(k) => setSort((s) => nextSort(s, k))}
                      />
                      <SortTh
                        label="Due"
                        k="dueDate"
                        sort={sort}
                        onSort={(k) => setSort((s) => nextSort(s, k))}
                      />
                      <SortTh
                        label="Status"
                        k="daysPastDue"
                        sort={sort}
                        onSort={(k) => setSort((s) => nextSort(s, k))}
                      />
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-400">
                        Action
                      </th>
                      <SortTh
                        label="Total"
                        k="total"
                        sort={sort}
                        onSort={(k) => setSort((s) => nextSort(s, k))}
                      />
                      <SortTh
                        label="Paid"
                        k="paid"
                        sort={sort}
                        onSort={(k) => setSort((s) => nextSort(s, k))}
                      />
                      <SortTh
                        label="Balance"
                        k="balance"
                        sort={sort}
                        onSort={(k) => setSort((s) => nextSort(s, k))}
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/60">
                    {sortedInvoices.map((i) => (
                      <tr key={i.id} className="hover:bg-gray-700/20">
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <span className="flex items-center gap-2">
                            <span
                              className={cls(
                                "w-1.5 h-4 rounded-sm",
                                AGING_BAR_6[i.bucket],
                              )}
                              title={labels[i.bucket]}
                            />
                            <span className="text-gray-100">
                              {i.invoiceNumber}
                            </span>
                          </span>
                        </td>
                        <td
                          className="px-3 py-2.5 text-gray-300 max-w-[200px] truncate"
                          title={i.customer}
                        >
                          {i.customer}
                        </td>
                        <td
                          className="px-3 py-2.5 text-gray-300 max-w-[240px] truncate"
                          title={i.projectName}
                        >
                          <span className="text-gray-500">
                            #{i.projectNumber}
                          </span>{" "}
                          {i.projectName}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-400 whitespace-nowrap">
                          {i.kind}
                        </td>
                        <td className="px-3 py-2.5 text-right text-gray-400 whitespace-nowrap tabular-nums">
                          {fmtDay(i.invoiceDate)}
                        </td>
                        <td className="px-3 py-2.5 text-right text-gray-400 whitespace-nowrap tabular-nums">
                          {fmtDay(i.dueDate)}
                        </td>
                        <td className="px-3 py-2.5 text-right text-xs whitespace-nowrap">
                          <PastDue days={i.daysPastDue} />
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-300 min-w-[220px]">
                          {actions[i.bucket]}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-300">
                          {money(i.total)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-blue-400">
                          {i.paid ? money(i.paid) : "—"}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-white">
                          {money(i.balance)}
                        </td>
                      </tr>
                    ))}
                    {sortedInvoices.length === 0 && (
                      <tr>
                        <td
                          colSpan={11}
                          className="px-4 py-12 text-center text-sm text-gray-500"
                        >
                          No open invoices for these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {sortedInvoices.length > 0 && (
                    <tfoot className="bg-gray-900/80 border-t border-gray-600">
                      <tr>
                        <td
                          colSpan={8}
                          className="px-3 py-3 text-sm font-semibold text-white"
                        >
                          Total · {sortedInvoices.length} invoices
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-gray-200">
                          {money(sum(sortedInvoices, "total"))}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-blue-400">
                          {money(sum(sortedInvoices, "paid"))}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-orange-300">
                          {money(sum(sortedInvoices, "balance"))}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}

            {view === "holdback" && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900/80">
                    <tr>
                      {[
                        "Project",
                        "Customer",
                        "Held on draws",
                        "Released",
                        "Draw holdback",
                        "CO invoice holdback",
                        "Total receivable",
                      ].map((h, i) => (
                        <th
                          key={h}
                          className={cls(
                            "px-3 py-2.5 text-xs font-medium text-gray-400 whitespace-nowrap",
                            i < 2 ? "text-left" : "text-right",
                          )}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/60">
                    {holdback.map((h) => (
                      <tr
                        key={String(h.projectId)}
                        className="hover:bg-gray-700/20"
                      >
                        <td className="px-3 py-2.5 text-gray-100">
                          <span className="text-gray-500">
                            #{h.projectNumber}
                          </span>{" "}
                          {h.projectName}
                        </td>
                        <td className="px-3 py-2.5 text-gray-300">
                          {h.customer}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-300">
                          {money(h.progressHeld)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-400">
                          {h.released ? money(h.released) : "—"}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-200">
                          {money(h.receivable)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-400">
                          {h.standaloneReceivable
                            ? money(h.standaloneReceivable)
                            : "—"}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-purple-300">
                          {money(h.totalReceivable)}
                        </td>
                      </tr>
                    ))}
                    {holdback.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-12 text-center text-sm text-gray-500"
                        >
                          No holdback is being retained.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {holdback.length > 0 && (
                    <tfoot className="bg-gray-900/80 border-t border-gray-600">
                      <tr>
                        <td
                          colSpan={2}
                          className="px-3 py-3 font-semibold text-white"
                        >
                          Total · {holdback.length} projects
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-gray-200">
                          {money(sum(holdback, "progressHeld"))}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-gray-300">
                          {money(sum(holdback, "released"))}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-gray-200">
                          {money(sum(holdback, "receivable"))}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-gray-300">
                          {money(sum(holdback, "standaloneReceivable"))}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-purple-300">
                          {money(holdbackTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ACCOUNTS PAYABLE
// ════════════════════════════════════════════════════════════════════════════
const SCHEDULE_GROUPS = [
  {
    key: "overdue",
    label: "Overdue",
    test: (d) => d > 0,
    tone: "text-red-400",
  },
  {
    key: "week",
    label: "Due within 7 days",
    test: (d) => d <= 0 && d >= -7,
    tone: "text-orange-300",
  },
  {
    key: "month",
    label: "Due in 8–30 days",
    test: (d) => d < -7 && d >= -30,
    tone: "text-yellow-300",
  },
  {
    key: "later",
    label: "Due later",
    test: (d) => d < -30,
    tone: "text-gray-300",
  },
];

function PayablesReport({ token }) {
  const [asOf, setAsOf] = useState(localISO(new Date()));
  const [ageBy, setAgeBy] = useState("due");
  const [terms, setTerms] = useState(30);
  const [scope, setScope] = useState("all");
  const [includePending, setIncludePending] = useState(true);
  const [view, setView] = useState("vendors");
  const [bucket, setBucket] = useState(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [sort, setSort] = useState({ key: "daysPastDue", dir: "desc" });

  const { data, loading, error } = useReport(
    "/finance3/ap",
    { asOf, ageBy, terms },
    token,
  );
  const labels = data?.bucketLabels || ["", "", "", "", ""];

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.bills || []).filter((b) => {
      if (scope !== "all" && b.chargedTo.type !== scope) return false;
      if (!includePending && !b.approved) return false;
      if (!q) return true;
      return [b.vendor, b.reference, b.billName, b.chargedTo.label].some((v) =>
        String(v ?? "")
          .toLowerCase()
          .includes(q),
      );
    });
  }, [data, search, scope, includePending]);

  const bucketTotals = useMemo(() => {
    const a = [0, 0, 0, 0, 0];
    searched.forEach((b) => (a[b.bucket] += b.balance));
    return a;
  }, [searched]);

  const visible = useMemo(
    () =>
      bucket == null ? searched : searched.filter((b) => b.bucket === bucket),
    [searched, bucket],
  );

  const vendors = useMemo(
    () =>
      groupAging(visible, {
        key: (b) => b.vendorId,
        name: (b) => b.vendor,
        subKey: (b) => b.id,
        subName: (b) =>
          `${b.reference} · due ${fmtDay(b.dueDate)} · ${b.chargedTo.label}`,
      }),
    [visible],
  );

  const sortedBills = useMemo(() => sortRows(visible, sort), [visible, sort]);
  const schedule = useMemo(
    () =>
      SCHEDULE_GROUPS.map((g) => {
        const items = visible
          .filter((b) => g.test(b.daysPastDue))
          .sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)));
        return { ...g, items, total: sum(items, "balance") };
      }),
    [visible],
  );

  const totalOpen = sum(searched, "balance");
  const overdue = searched.filter((b) => b.daysPastDue > 0);
  const dueWeek = searched.filter(
    (b) => b.daysPastDue <= 0 && b.daysPastDue >= -7,
  );
  const pending = (data?.bills || []).filter(
    (b) => !b.approved && (scope === "all" || b.chargedTo.type === scope),
  );

  const toggle = (k) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });

  const exportCsv = () => {
    const stamp = `ap-${view}-${asOf}.csv`;
    if (view === "vendors") {
      const rows = [];
      vendors.forEach((g) => {
        rows.push([
          g.name,
          "",
          ...g.buckets.map(csvNum),
          csvNum(g.total),
          g.count,
        ]);
        g.subs.forEach((s) =>
          rows.push([
            g.name,
            s.name,
            ...s.buckets.map(csvNum),
            csvNum(s.total),
            s.count,
          ]),
        );
      });
      downloadCsv(stamp, ["Vendor", "Bill", ...labels, "Total", "Bills"], rows);
    } else {
      const list =
        view === "schedule"
          ? schedule.flatMap((g) =>
              g.items.map((b) => ({ ...b, group: g.label })),
            )
          : sortedBills;
      downloadCsv(
        stamp,
        [
          "Vendor",
          "Bill / invoice #",
          "Bill name",
          "Charged to",
          "Type",
          "Invoice date",
          "Due date",
          "Days past due",
          "Aging bucket",
          "Schedule",
          "Total",
          "Credits",
          "Paid",
          "Balance",
          "Approved",
        ],
        list.map((b) => [
          b.vendor,
          b.reference,
          b.billName,
          b.chargedTo.label,
          b.chargedTo.type,
          b.invoiceDate,
          b.dueDate,
          b.daysPastDue,
          labels[b.bucket],
          b.group || "",
          csvNum(b.total),
          csvNum(b.credits),
          csvNum(b.paid),
          csvNum(b.balance),
          b.approved ? "Yes" : "Awaiting approval",
        ]),
      );
    }
  };

  const BillRow = ({ b }) => (
    <tr className="hover:bg-gray-700/20">
      <td className="px-3 py-2.5 whitespace-nowrap">
        <span className="flex items-center gap-2">
          <span
            className={cls("w-1.5 h-4 rounded-sm", AGING_BAR[b.bucket])}
            title={labels[b.bucket]}
          />
          <span
            className="text-gray-100 max-w-[180px] truncate"
            title={b.vendor}
          >
            {b.vendor}
          </span>
        </span>
      </td>
      <td
        className="px-3 py-2.5 text-gray-300 max-w-[200px] truncate"
        title={b.billName}
      >
        {b.reference}
        {!b.approved && (
          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-yellow-900/40 text-yellow-300 border border-yellow-700/40">
            Awaiting approval
          </span>
        )}
      </td>
      <td
        className="px-3 py-2.5 text-xs max-w-[220px] truncate"
        title={b.chargedTo.label}
      >
        <span
          className={
            b.chargedTo.type === "overhead"
              ? "text-sky-300"
              : b.chargedTo.type === "project"
                ? "text-gray-300"
                : "text-yellow-300"
          }
        >
          {b.chargedTo.label}
        </span>
      </td>
      <td className="px-3 py-2.5 text-right text-gray-400 whitespace-nowrap tabular-nums">
        {fmtDay(b.invoiceDate)}
      </td>
      <td className="px-3 py-2.5 text-right text-gray-400 whitespace-nowrap tabular-nums">
        {fmtDay(b.dueDate)}
      </td>
      <td className="px-3 py-2.5 text-right text-xs whitespace-nowrap">
        <PastDue days={b.daysPastDue} />
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums text-gray-300">
        {money(b.total)}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums text-gray-400">
        {b.credits || b.paid ? money(b.credits + b.paid) : "—"}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-white">
        {money(b.balance)}
      </td>
    </tr>
  );

  const BillHead = ({ sortable }) => (
    <thead className="bg-gray-900/80">
      <tr>
        {sortable ? (
          <>
            <SortTh
              label="Vendor"
              k="vendor"
              sort={sort}
              onSort={(k) => setSort((s) => nextSort(s, k))}
              align="left"
            />
            <SortTh
              label="Bill"
              k="reference"
              sort={sort}
              onSort={(k) => setSort((s) => nextSort(s, k))}
              align="left"
            />
            <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-400">
              Charged to
            </th>
            <SortTh
              label="Invoiced"
              k="invoiceDate"
              sort={sort}
              onSort={(k) => setSort((s) => nextSort(s, k))}
            />
            <SortTh
              label="Due"
              k="dueDate"
              sort={sort}
              onSort={(k) => setSort((s) => nextSort(s, k))}
            />
            <SortTh
              label="Status"
              k="daysPastDue"
              sort={sort}
              onSort={(k) => setSort((s) => nextSort(s, k))}
            />
            <SortTh
              label="Total"
              k="total"
              sort={sort}
              onSort={(k) => setSort((s) => nextSort(s, k))}
            />
            <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-400">
              Credits + paid
            </th>
            <SortTh
              label="Balance"
              k="balance"
              sort={sort}
              onSort={(k) => setSort((s) => nextSort(s, k))}
            />
          </>
        ) : (
          [
            "Vendor",
            "Bill",
            "Charged to",
            "Invoiced",
            "Due",
            "Status",
            "Total",
            "Credits + paid",
            "Balance",
          ].map((h, i) => (
            <th
              key={h}
              className={cls(
                "px-3 py-2.5 text-xs font-medium text-gray-400 whitespace-nowrap",
                i < 3 ? "text-left" : "text-right",
              )}
            >
              {h}
            </th>
          ))
        )}
      </tr>
    </thead>
  );

  return (
    <div className="space-y-6">
      <AgingControls
        asOf={asOf}
        setAsOf={setAsOf}
        ageBy={ageBy}
        setAgeBy={setAgeBy}
        terms={terms}
        setTerms={setTerms}
        search={search}
        setSearch={setSearch}
        placeholder="Vendor, bill or project"
      >
        <Field label="Charged to">
          <Segmented
            value={scope}
            onChange={setScope}
            options={[
              { key: "all", label: "All" },
              { key: "project", label: "Projects" },
              { key: "overhead", label: "Overhead" },
              { key: "unassigned", label: "Unassigned" },
            ]}
          />
        </Field>
        <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer select-none pb-2">
          <input
            type="checkbox"
            checked={includePending}
            onChange={(e) => setIncludePending(e.target.checked)}
            className="accent-orange-500"
          />
          Include bills awaiting approval
        </label>
      </AgingControls>

      {error && <Notice>{error}</Notice>}
      {!data && loading && (
        <div className="py-16 text-center text-sm text-gray-400">
          Loading payables…
        </div>
      )}

      {data && (
        <div
          className={cls(
            "space-y-6 transition-opacity",
            loading && "opacity-60",
          )}
        >
          <Card className="p-5 space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <Stat
                label="Accounts payable"
                value={money0(totalOpen)}
                sub={`${searched.length} open bills as of ${fmtDay(data.asOf)}`}
                tone="text-orange-300"
              />
              <Stat
                label="Overdue"
                value={money0(sum(overdue, "balance"))}
                sub={`${overdue.length} bills past their due date`}
                tone={overdue.length ? "text-red-400" : "text-emerald-400"}
              />
              <Stat
                label="Due in the next 7 days"
                value={money0(sum(dueWeek, "balance"))}
                sub={`${dueWeek.length} bills`}
                tone="text-yellow-300"
              />
              <Stat
                label="Awaiting approval"
                value={money0(sum(pending, "balance"))}
                sub={
                  includePending
                    ? `${pending.length} bills, included above`
                    : `${pending.length} bills, left out above`
                }
                tone="text-gray-200"
              />
            </div>
            <AgingBand
              labels={labels}
              amounts={bucketTotals}
              active={bucket}
              onSelect={setBucket}
            />
          </Card>

          <Card className="overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-700 flex flex-wrap items-center justify-between gap-3">
              <Segmented
                value={view}
                onChange={setView}
                options={[
                  { key: "vendors", label: "Aging by vendor" },
                  { key: "bills", label: "Open bills" },
                  { key: "schedule", label: "Payment schedule" },
                ]}
              />
              <ExportButton onClick={exportCsv} disabled={!visible.length} />
            </div>

            {view === "vendors" && (
              <AgingSummaryTable
                groups={vendors}
                labels={labels}
                entity="Vendor"
                countLabel="Bills"
                expanded={expanded}
                onToggle={toggle}
              />
            )}

            {view === "bills" && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <BillHead sortable />
                  <tbody className="divide-y divide-gray-700/60">
                    {sortedBills.map((b) => (
                      <BillRow key={b.id} b={b} />
                    ))}
                    {sortedBills.length === 0 && (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-4 py-12 text-center text-sm text-gray-500"
                        >
                          No open bills for these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {sortedBills.length > 0 && (
                    <tfoot className="bg-gray-900/80 border-t border-gray-600">
                      <tr>
                        <td
                          colSpan={6}
                          className="px-3 py-3 font-semibold text-white"
                        >
                          Total · {sortedBills.length} bills
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-gray-200">
                          {money(sum(sortedBills, "total"))}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-gray-300">
                          {money(
                            sum(sortedBills, "credits") +
                              sum(sortedBills, "paid"),
                          )}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-orange-300">
                          {money(sum(sortedBills, "balance"))}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}

            {view === "schedule" && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <BillHead />
                  {schedule.map((g) => (
                    <tbody key={g.key} className="divide-y divide-gray-700/60">
                      <tr className="bg-gray-900/60">
                        <td
                          colSpan={8}
                          className={cls(
                            "px-3 py-2 text-sm font-semibold",
                            g.tone,
                          )}
                        >
                          {g.label}{" "}
                          <span className="text-xs font-normal text-gray-500 ml-1">
                            {g.items.length} bills
                          </span>
                        </td>
                        <td
                          className={cls(
                            "px-3 py-2 text-right tabular-nums font-semibold",
                            g.tone,
                          )}
                        >
                          {money(g.total)}
                        </td>
                      </tr>
                      {g.items.map((b) => (
                        <BillRow key={b.id} b={b} />
                      ))}
                      {g.items.length === 0 && (
                        <tr>
                          <td
                            colSpan={9}
                            className="px-3 py-3 text-xs text-gray-600"
                          >
                            None
                          </td>
                        </tr>
                      )}
                    </tbody>
                  ))}
                </table>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// OVERHEAD
// ════════════════════════════════════════════════════════════════════════════
function computeOverheadRange(mode, months, sel, today) {
  if (!months.length) return null;
  const first = monthBounds(months[0].key)[0];
  const last = monthBounds(months[months.length - 1].key)[1];
  if (mode === "month") {
    if (!sel.month) return null;
    const [from, to] = monthBounds(sel.month);
    return {
      from,
      to,
      label: months.find((m) => m.key === sel.month)?.label || sel.month,
    };
  }
  if (mode === "quarter") {
    const slice = months.slice((sel.quarter - 1) * 3, sel.quarter * 3);
    if (!slice.length) return null;
    return {
      from: monthBounds(slice[0].key)[0],
      to: monthBounds(slice[slice.length - 1].key)[1],
      label: `Fiscal Q${sel.quarter} · ${slice[0].label} – ${slice[slice.length - 1].label}`,
    };
  }
  if (mode === "ytd") {
    const to = today < first ? last : today > last ? last : today;
    return { from: first, to, label: `Year to date through ${fmtDay(to)}` };
  }
  if (mode === "year")
    return { from: first, to: last, label: "Full fiscal year" };
  if (!sel.from || !sel.to || sel.from > sel.to) return null;
  return {
    from: sel.from,
    to: sel.to,
    label: `${fmtDay(sel.from)} – ${fmtDay(sel.to)}`,
  };
}

const varianceTone = (v) =>
  v < -0.005
    ? "text-red-400"
    : v > 0.005
      ? "text-emerald-400"
      : "text-gray-500";

function UsedBar({ pct, expected }) {
  if (pct == null)
    return <span className="text-gray-600 text-xs">No budget</span>;
  const tone =
    pct > 100
      ? "bg-red-500"
      : pct > expected + 10
        ? "bg-orange-400"
        : "bg-emerald-500";
  return (
    <div className="flex items-center gap-2 justify-end">
      <div
        className="relative w-24 h-1.5 bg-gray-700 rounded-full overflow-visible"
        title={`${pct.toFixed(1)}% used · ${expected.toFixed(0)}% of the year elapsed`}
      >
        <div
          className={cls("h-full rounded-full", tone)}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
        <div
          className="absolute -top-1 w-px h-3.5 bg-gray-200/70"
          style={{ left: `${Math.min(100, expected)}%` }}
          aria-hidden="true"
        />
      </div>
      <span
        className={cls(
          "text-xs tabular-nums w-12 text-right",
          pct > 100 ? "text-red-400" : "text-gray-300",
        )}
      >
        {pct.toFixed(0)}%
      </span>
    </div>
  );
}

function LedgerDrawer({ token, budgetId, account, from, to, onClose }) {
  const { data, loading, error } = useReport(
    "/finance3/overhead/ledger",
    { budgetId, categoryId: String(account._id), from, to },
    token,
  );

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const exportCsv = () =>
    downloadCsv(
      `ledger-${account.accountNumber}-${from}-${to}.csv`,
      [
        "Date",
        "Type",
        "Reference",
        "Payee",
        "Memo",
        "Account",
        "Amount",
        "Running balance",
      ],
      (data?.lines || []).map((l) => [
        l.date,
        l.type,
        l.reference,
        l.payee,
        l.memo,
        l.account,
        csvNum(l.amount),
        csvNum(l.balance),
      ]),
    );

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={`Ledger for ${account.accountNumber} ${account.name}`}
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-label="Close ledger"
      />
      <div className="relative w-full max-w-4xl h-full bg-gray-900 border-l border-gray-700 flex flex-col">
        <div className="px-5 py-4 border-b border-gray-700 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs text-orange-300 tabular-nums">
              {account.accountNumber}
            </div>
            <h3 className="text-lg font-semibold text-white">{account.name}</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {fmtDay(from)} – {fmtDay(to)}
              {data?.account?.includesSubaccounts && " · includes subaccounts"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton onClick={exportCsv} disabled={!data?.lines?.length} />
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-600 text-gray-300 hover:text-white hover:border-gray-400"
            >
              Close
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {error && (
            <div className="p-5">
              <Notice>{error}</Notice>
            </div>
          )}
          {loading && !data && (
            <div className="py-16 text-center text-sm text-gray-400">
              Loading ledger…
            </div>
          )}
          {data && (
            <table className="w-full text-sm">
              <thead className="bg-gray-800 sticky top-0">
                <tr>
                  {[
                    "Date",
                    "Type",
                    "Reference",
                    "Payee / memo",
                    "Account",
                    "Amount",
                    "Balance",
                  ].map((h, i) => (
                    <th
                      key={h}
                      className={cls(
                        "px-3 py-2.5 text-xs font-medium text-gray-400 whitespace-nowrap",
                        i >= 5 ? "text-right" : "text-left",
                      )}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {data.lines.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-800/60">
                    <td className="px-3 py-2 text-gray-400 whitespace-nowrap tabular-nums">
                      {fmtDay(l.date)}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-400 whitespace-nowrap">
                      {l.type}
                    </td>
                    <td
                      className="px-3 py-2 text-gray-300 max-w-[160px] truncate"
                      title={l.reference}
                    >
                      {l.reference}
                    </td>
                    <td className="px-3 py-2 max-w-[260px]">
                      <div className="text-gray-200 truncate" title={l.payee}>
                        {l.payee || "—"}
                      </div>
                      {l.memo && (
                        <div
                          className="text-[11px] text-gray-500 truncate"
                          title={l.memo}
                        >
                          {l.memo}
                        </div>
                      )}
                    </td>
                    <td
                      className="px-3 py-2 text-xs text-gray-400 max-w-[160px] truncate"
                      title={l.account}
                    >
                      {l.account}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-100">
                      {money(l.amount)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-400">
                      {money(l.balance)}
                    </td>
                  </tr>
                ))}
                {data.lines.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-12 text-center text-sm text-gray-500"
                    >
                      Nothing was posted to this account in this period.
                    </td>
                  </tr>
                )}
              </tbody>
              {data.lines.length > 0 && (
                <tfoot className="bg-gray-800 border-t border-gray-600 sticky bottom-0">
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-3 font-semibold text-white"
                    >
                      Total · {data.lines.length} entries
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold text-orange-300">
                      {money(data.total)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function OverheadReport({ token }) {
  const [query, setQuery] = useState({
    budgetId: undefined,
    from: undefined,
    to: undefined,
  });
  const [mode, setMode] = useState("ytd");
  const [sel, setSel] = useState({ month: null, quarter: 1, from: "", to: "" });
  const [view, setView] = useState("statement");
  const [showEmpty, setShowEmpty] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [ledgerAccount, setLedgerAccount] = useState(null);

  const { data, loading, error } = useReport(
    "/finance3/overhead/report",
    query,
    token,
  );
  const months = data?.months || [];
  const monthsKey = `${data?.budget?._id || ""}|${months.map((m) => m.key).join(",")}`;
  const today = localISO(new Date());
  const nowKey = today.slice(0, 7);
  const effectiveMonth = months.some((m) => m.key === sel.month)
    ? sel.month
    : months.find((m) => m.key === nowKey)?.key ||
      months[months.length - 1]?.key ||
      null;
  const quarterCount = Math.max(1, Math.ceil(months.length / 3));

  const range = useMemo(
    () =>
      computeOverheadRange(
        mode,
        months,
        { ...sel, month: effectiveMonth },
        today,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, monthsKey, sel, effectiveMonth, today],
  );

  // Keep the fetched period in step with the controls
  useEffect(() => {
    if (!data?.budget || !range) return;
    if (range.from === data.range.from && range.to === data.range.to) return;
    setQuery({
      budgetId: String(data.budget._id),
      from: range.from,
      to: range.to,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.from, range?.to, data?.budget?._id]);

  const changeMode = (m) => {
    if (m === "custom" && data?.range && !sel.from)
      setSel((s) => ({ ...s, from: data.range.from, to: data.range.to }));
    setMode(m);
  };

  const rows = data?.rows || [];
  const parentOf = useMemo(
    () => new Map(rows.map((r) => [String(r._id), r.parentId])),
    [rows],
  );
  const visibleRows = useMemo(() => {
    const hidden = (r) => {
      let p = r.parentId;
      let guard = 0;
      while (p && guard < 50) {
        if (collapsed.has(p)) return true;
        p = parentOf.get(p);
        guard += 1;
      }
      return false;
    };
    const empty = (r) =>
      [r.period, r.ytd, r.annualBudget, r.fy].every((v) => Math.abs(v) < 0.005);
    return rows.filter(
      (r) => !hidden(r) && (showEmpty || !empty(r)) && (r.active || !empty(r)),
    );
  }, [rows, collapsed, parentOf, showEmpty]);

  const toggleCollapse = (id) =>
    setCollapsed((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const t = data?.totals;
  const expectedPct = (data?.ytdFraction || 0) * 100;

  const exportCsv = () => {
    const indent = (r) => `${"  ".repeat(r.depth)}${r.name}`;
    if (view === "statement") {
      downloadCsv(
        `overhead-budget-vs-actual-${data.range.from}-${data.range.to}.csv`,
        [
          "Account #",
          "Account",
          "Period actual",
          "Period budget",
          "Period variance",
          "YTD actual",
          "YTD budget",
          "YTD variance",
          "Annual budget",
          "% of annual used",
        ],
        [
          ...rows.map((r) => [
            r.accountNumber,
            indent(r),
            csvNum(r.period),
            csvNum(r.periodBudget),
            csvNum(r.periodVariance),
            csvNum(r.ytd),
            csvNum(r.ytdBudget),
            csvNum(r.ytdVariance),
            csvNum(r.annualBudget),
            r.usedPct == null ? "" : r.usedPct.toFixed(1),
          ]),
          [
            "",
            "Total",
            csvNum(t.period),
            csvNum(t.periodBudget),
            csvNum(t.periodVariance),
            csvNum(t.ytd),
            csvNum(t.ytdBudget),
            csvNum(t.ytdVariance),
            csvNum(t.annualBudget),
            t.usedPct == null ? "" : t.usedPct.toFixed(1),
          ],
        ],
      );
    } else {
      downloadCsv(
        `overhead-by-month-FY${data.budget.fiscalYear}.csv`,
        [
          "Account #",
          "Account",
          ...months.map((m) => m.label),
          "Fiscal year total",
          "Annual budget",
        ],
        [
          ...rows.map((r) => [
            r.accountNumber,
            indent(r),
            ...months.map((m) => csvNum(r.months[m.key] || 0)),
            csvNum(r.fy),
            csvNum(r.annualBudget),
          ]),
          [
            "",
            "Total",
            ...months.map((m) => csvNum(t.months[m.key] || 0)),
            csvNum(t.fy),
            csvNum(t.annualBudget),
          ],
        ],
      );
    }
  };

  if (data && !data.budget)
    return (
      <Card className="p-10 text-center">
        <p className="text-sm text-gray-300">No overhead budgets yet.</p>
        <p className="text-xs text-gray-500 mt-1">
          Create a fiscal-year budget on the Overhead page and its accounts will
          report here.
        </p>
      </Card>
    );

  const AccountCell = ({ r }) => (
    <td className="px-3 py-2.5 sticky left-0 z-10 bg-gray-800">
      <div
        className="flex items-center gap-1.5 min-w-[260px]"
        style={{ paddingLeft: r.depth * 18 }}
      >
        {r.hasChildren ? (
          <button
            type="button"
            onClick={() => toggleCollapse(String(r._id))}
            aria-label={
              collapsed.has(String(r._id))
                ? "Expand subaccounts"
                : "Collapse subaccounts"
            }
            className="p-0.5 rounded text-gray-500 hover:text-orange-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
          >
            <svg
              className={cls(
                "w-3.5 h-3.5 transition-transform",
                !collapsed.has(String(r._id)) && "rotate-90",
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        ) : (
          <span className="w-[18px]" />
        )}
        <span className="text-xs tabular-nums text-gray-500 w-16 shrink-0">
          {r.accountNumber}
        </span>
        <button
          type="button"
          onClick={() => setLedgerAccount(r)}
          className={cls(
            "text-left truncate hover:text-orange-300 hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 rounded",
            r.depth === 0 ? "text-white font-medium" : "text-gray-200",
            !r.active && "text-gray-500 line-through",
          )}
          title="Open ledger"
        >
          {r.name}
        </button>
      </div>
    </td>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Fiscal year">
          <select
            value={data?.budget?._id ? String(data.budget._id) : ""}
            onChange={(e) => {
              setMode("ytd");
              setQuery({
                budgetId: e.target.value,
                from: undefined,
                to: undefined,
              });
            }}
            className={INPUT}
            disabled={!data}
          >
            {(data?.budgets || []).map((b) => (
              <option key={String(b._id)} value={String(b._id)}>
                {b.label || `FY${b.fiscalYear}`}
                {b.status && b.status !== "active" ? ` (${b.status})` : ""}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Period">
          <Segmented
            value={mode}
            onChange={changeMode}
            options={[
              { key: "month", label: "Month" },
              { key: "quarter", label: "Quarter" },
              { key: "ytd", label: "Year to date" },
              { key: "year", label: "Full year" },
              { key: "custom", label: "Custom" },
            ]}
          />
        </Field>
        {mode === "month" && (
          <Field label="Month">
            <select
              value={effectiveMonth || ""}
              onChange={(e) => setSel((s) => ({ ...s, month: e.target.value }))}
              className={INPUT}
            >
              {months.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </Field>
        )}
        {mode === "quarter" && (
          <Field label="Fiscal quarter">
            <Segmented
              value={String(sel.quarter)}
              onChange={(q) => setSel((s) => ({ ...s, quarter: Number(q) }))}
              options={Array.from({ length: quarterCount }, (_, i) => ({
                key: String(i + 1),
                label: `Q${i + 1}`,
              }))}
            />
          </Field>
        )}
        {mode === "custom" && (
          <>
            <Field label="From">
              <input
                type="date"
                value={sel.from}
                onChange={(e) =>
                  setSel((s) => ({ ...s, from: e.target.value }))
                }
                className={INPUT}
              />
            </Field>
            <Field label="To">
              <input
                type="date"
                value={sel.to}
                onChange={(e) => setSel((s) => ({ ...s, to: e.target.value }))}
                className={INPUT}
              />
            </Field>
          </>
        )}
      </div>

      {error && <Notice>{error}</Notice>}
      {mode === "custom" && !range && (
        <Notice tone="warn">
          Pick a start date that's on or before the end date.
        </Notice>
      )}
      {!data && loading && (
        <div className="py-16 text-center text-sm text-gray-400">
          Loading overhead…
        </div>
      )}

      {data?.budget && t && (
        <div
          className={cls(
            "space-y-6 transition-opacity",
            loading && "opacity-60",
          )}
        >
          <Card className="p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5">
              <div>
                <div className="text-2xl font-bold text-white tracking-tight">
                  {data.budget.label || `FY${data.budget.fiscalYear} Overhead`}
                </div>
                <div className="text-sm text-gray-400 mt-0.5">
                  {range?.label} · {fmtDay(data.range.from)} –{" "}
                  {fmtDay(data.range.to)}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Fiscal year {fmtDay(data.range.fiscalStart)} –{" "}
                {fmtDay(data.range.fiscalEnd)} · {expectedPct.toFixed(0)}%
                elapsed
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <Stat
                label="Actual this period"
                value={money0(t.period)}
                sub={`Budget ${money0(t.periodBudget)}`}
                tone="text-orange-300"
              />
              <Stat
                label={
                  t.periodVariance >= 0
                    ? "Under budget this period"
                    : "Over budget this period"
                }
                value={money0(Math.abs(t.periodVariance))}
                sub={
                  t.periodBudget > 0
                    ? `${((t.period / t.periodBudget) * 100).toFixed(0)}% of period budget spent`
                    : "No budget for this period"
                }
                tone={varianceTone(t.periodVariance)}
              />
              <Stat
                label="Year to date"
                value={money0(t.ytd)}
                sub={`YTD budget ${money0(t.ytdBudget)} · ${pctText(t.usedPct)} of annual`}
              />
              <Stat
                label="Annual budget remaining"
                value={money0(t.annualBudget - t.ytd)}
                sub={`of ${money0(t.annualBudget)}`}
                tone={
                  t.annualBudget - t.ytd < 0 ? "text-red-400" : "text-gray-100"
                }
              />
            </div>

            <div className="mt-5 pt-4 border-t border-gray-700 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-gray-400">
              <span className="text-gray-500">This period by source</span>
              <span>
                Bills{" "}
                <span className="text-gray-100 tabular-nums ml-1">
                  {money0(t.bill)}
                </span>
              </span>
              <span>
                Payroll{" "}
                <span className="text-gray-100 tabular-nums ml-1">
                  {money0(t.payroll)}
                </span>
              </span>
              <span>
                QuickBooks, CSV and manual{" "}
                <span className="text-gray-100 tabular-nums ml-1">
                  {money0(t.transaction)}
                </span>
              </span>
            </div>

            {Math.abs(data.outsideFiscalYear) > 0.5 && (
              <div className="mt-4">
                <Notice tone="warn">
                  {money(data.outsideFiscalYear)} is posted to this budget but
                  dated outside its fiscal year, so it isn't in the figures
                  above. The Overhead page counts it; check those entries'
                  dates.
                </Notice>
              </div>
            )}
          </Card>

          <Card className="overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-700 flex flex-wrap items-center justify-between gap-3">
              <Segmented
                value={view}
                onChange={setView}
                options={[
                  { key: "statement", label: "Budget vs actual" },
                  { key: "monthly", label: "By month" },
                ]}
              />
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showEmpty}
                    onChange={(e) => setShowEmpty(e.target.checked)}
                    className="accent-orange-500"
                  />
                  Show accounts with no activity
                </label>
                <ExportButton onClick={exportCsv} disabled={!rows.length} />
              </div>
            </div>

            {view === "statement" && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900/80">
                    <tr className="border-b border-gray-700/60">
                      <th className="sticky left-0 z-10 bg-gray-900" />
                      <th
                        colSpan={3}
                        className="px-3 pt-2.5 pb-1 text-center text-xs font-medium text-orange-300 border-l border-gray-700/60"
                      >
                        {range?.label || "This period"}
                      </th>
                      <th
                        colSpan={3}
                        className="px-3 pt-2.5 pb-1 text-center text-xs font-medium text-gray-300 border-l border-gray-700/60"
                      >
                        Year to date
                      </th>
                      <th
                        colSpan={2}
                        className="px-3 pt-2.5 pb-1 text-center text-xs font-medium text-gray-300 border-l border-gray-700/60"
                      >
                        Fiscal year
                      </th>
                    </tr>
                    <tr>
                      <th className="sticky left-0 z-10 bg-gray-900 px-3 py-2 text-left text-xs font-medium text-gray-400">
                        Account
                      </th>
                      {[
                        "Actual",
                        "Budget",
                        "Variance",
                        "Actual",
                        "Budget",
                        "Variance",
                        "Budget",
                        "Used",
                      ].map((h, i) => (
                        <th
                          key={i}
                          className={cls(
                            "px-3 py-2 text-right text-xs font-medium text-gray-400 whitespace-nowrap",
                            (i === 0 || i === 3 || i === 6) &&
                              "border-l border-gray-700/60",
                          )}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/50">
                    {visibleRows.map((r) => (
                      <tr
                        key={String(r._id)}
                        className={cls(
                          r.depth === 0 ? "bg-gray-800" : "bg-gray-800/60",
                          "hover:bg-gray-700/30",
                        )}
                      >
                        <AccountCell r={r} />
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-100 border-l border-gray-700/40">
                          {money(r.period)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-400">
                          {money(r.periodBudget)}
                        </td>
                        <td
                          className={cls(
                            "px-3 py-2.5 text-right tabular-nums",
                            varianceTone(r.periodVariance),
                          )}
                        >
                          {money(r.periodVariance)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-100 border-l border-gray-700/40">
                          {money(r.ytd)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-400">
                          {money(r.ytdBudget)}
                        </td>
                        <td
                          className={cls(
                            "px-3 py-2.5 text-right tabular-nums",
                            varianceTone(r.ytdVariance),
                          )}
                        >
                          {money(r.ytdVariance)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-300 border-l border-gray-700/40">
                          {money(r.annualBudget)}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <UsedBar pct={r.usedPct} expected={expectedPct} />
                        </td>
                      </tr>
                    ))}
                    {visibleRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-4 py-12 text-center text-sm text-gray-500"
                        >
                          No account has a budget or activity in this fiscal
                          year. Turn on “Show accounts with no activity” to see
                          them all.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-gray-900/80 border-t border-gray-600">
                    <tr>
                      <td className="sticky left-0 z-10 bg-gray-900 px-3 py-3 font-semibold text-white">
                        Total overhead
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold text-orange-300 border-l border-gray-700/40">
                        {money(t.period)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold text-gray-300">
                        {money(t.periodBudget)}
                      </td>
                      <td
                        className={cls(
                          "px-3 py-3 text-right tabular-nums font-semibold",
                          varianceTone(t.periodVariance),
                        )}
                      >
                        {money(t.periodVariance)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold text-white border-l border-gray-700/40">
                        {money(t.ytd)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold text-gray-300">
                        {money(t.ytdBudget)}
                      </td>
                      <td
                        className={cls(
                          "px-3 py-3 text-right tabular-nums font-semibold",
                          varianceTone(t.ytdVariance),
                        )}
                      >
                        {money(t.ytdVariance)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold text-gray-200 border-l border-gray-700/40">
                        {money(t.annualBudget)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <UsedBar pct={t.usedPct} expected={expectedPct} />
                      </td>
                    </tr>
                  </tfoot>
                </table>
                <p className="px-5 py-3 text-[11px] text-gray-500 border-t border-gray-700/60">
                  Period and YTD budgets are the annual budget spread evenly
                  across the fiscal year's days. Variance is budget minus
                  actual, so a negative number means over budget. The tick on
                  each bar marks how much of the year has passed. Click an
                  account name to see every entry behind it.
                </p>
              </div>
            )}

            {view === "monthly" && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-900/80">
                    <tr>
                      <th className="sticky left-0 z-10 bg-gray-900 px-3 py-2.5 text-left font-medium text-gray-400">
                        Account
                      </th>
                      {months.map((m) => (
                        <th
                          key={m.key}
                          className={cls(
                            "px-2.5 py-2.5 text-right font-medium whitespace-nowrap",
                            m.key > nowKey
                              ? "text-gray-600"
                              : m.key === nowKey
                                ? "text-orange-300"
                                : "text-gray-400",
                          )}
                        >
                          {m.label.split(" ")[0]}
                        </th>
                      ))}
                      <th className="px-3 py-2.5 text-right font-medium text-gray-300 border-l border-gray-700/60">
                        FY total
                      </th>
                      <th className="px-3 py-2.5 text-right font-medium text-gray-400">
                        Budget
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/50">
                    {visibleRows.map((r) => (
                      <tr
                        key={String(r._id)}
                        className={cls(
                          r.depth === 0 ? "bg-gray-800" : "bg-gray-800/60",
                          "hover:bg-gray-700/30",
                        )}
                      >
                        <AccountCell r={r} />
                        {months.map((m) => {
                          const v = r.months[m.key] || 0;
                          return (
                            <td
                              key={m.key}
                              className={cls(
                                "px-2.5 py-2.5 text-right tabular-nums whitespace-nowrap",
                                Math.abs(v) < 0.005
                                  ? "text-gray-700"
                                  : "text-gray-200",
                              )}
                            >
                              {Math.abs(v) < 0.005 ? "—" : money0(v)}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2.5 text-right tabular-nums font-medium text-gray-100 border-l border-gray-700/40">
                          {money0(r.fy)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-400">
                          {money0(r.annualBudget)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-900/80 border-t border-gray-600">
                    <tr>
                      <td className="sticky left-0 z-10 bg-gray-900 px-3 py-3 font-semibold text-white text-sm">
                        Total overhead
                      </td>
                      {months.map((m) => (
                        <td
                          key={m.key}
                          className="px-2.5 py-3 text-right tabular-nums font-semibold text-gray-100 whitespace-nowrap"
                        >
                          {money0(t.months[m.key] || 0)}
                        </td>
                      ))}
                      <td className="px-3 py-3 text-right tabular-nums font-semibold text-orange-300 border-l border-gray-700/40">
                        {money0(t.fy)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold text-gray-300">
                        {money0(t.annualBudget)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {ledgerAccount && data?.budget && (
        <LedgerDrawer
          token={token}
          budgetId={String(data.budget._id)}
          account={ledgerAccount}
          from={data.range.from}
          to={data.range.to}
          onClose={() => setLedgerAccount(null)}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB SHELL
// ════════════════════════════════════════════════════════════════════════════
const REPORTS = [
  {
    key: "ar",
    label: "Accounts receivable",
    blurb:
      "What customers owe on V3 projects, aged, plus holdback still to be released.",
  },
  {
    key: "ap",
    label: "Accounts payable",
    blurb: "Unpaid bills company-wide, aged by vendor, with what's coming due.",
  },
  {
    key: "overhead",
    label: "Overhead",
    blurb:
      "Overhead accounts against budget by period, with the ledger behind each account.",
  },
];

export default function AccountingReports3() {
  const token = localStorage.getItem("token");
  const [tab, setTab] = useState("ar");
  const current = REPORTS.find((r) => r.key === tab);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Segmented size="md" value={tab} onChange={setTab} options={REPORTS} />
        <p className="text-sm text-gray-400">{current.blurb}</p>
      </div>
      {tab === "ar" && <ReceivablesReport token={token} />}
      {tab === "ap" && <PayablesReport token={token} />}
      {tab === "overhead" && <OverheadReport token={token} />}
    </div>
  );
}
